import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn';

import { Label } from './Label';

export interface ReasonOption {
  value: string;
  label: string;
}

export interface ReasonCodePickerProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly ReasonOption[];
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Mandatory reason selector for audit-logged actions (refunds, adjustments, etc.).
 * Labels are pre-translated strings from the feature namespace.
 */
export function ReasonCodePicker({
  name,
  value,
  onChange,
  options,
  required,
  disabled,
  className,
}: ReasonCodePickerProps) {
  const { t } = useTranslation('common');
  const id = useId();
  const selectId = `${id}-reason`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={selectId} required={required}>
        {t('ui.reasonCode.label')}
      </Label>
      <select
        id={selectId}
        name={name}
        value={value}
        required={required}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-10 w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-fg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <option value="">{t('ui.reasonCode.placeholder')}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
